import { getTaskStatusLabelKey } from '../../utils/task-display.utils';
import { getTaskStatusMeaning } from '../../utils/status-indicator.utils';
import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  model,
  computed,
  signal,
  inject,
  untracked,
  DestroyRef,
  effect,
  OnDestroy,
  viewChild,
  ElementRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ActionGlyphComponent } from '../action-glyph/action-glyph';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DetailPanelComponent } from '../detail-panel/detail-panel';
import { EditablePanelTitleComponent } from '../editable-panel-title/editable-panel-title';
import { PanelTabsComponent } from '../panel-tabs/panel-tabs';
import type { PanelTab } from '../panel-tabs/panel-tabs';
import { AuditTimelineComponent } from '../audit-timeline/audit-timeline';
import { RefreshButtonComponent } from '../refresh-button/refresh-button';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { DatePickerDirective } from '../../shared/directives';
import { SectionIconComponent } from '../section-icon/section-icon';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator';
import { BadgeComponent } from '../badge/badge';
import { InlineTextComponent } from '../inline-text/inline-text';
import {
  SelectComponent,
  type SelectOption,
  type SelectValue,
  coerceSingleSelectValue,
} from '../select/select';
import {
  Task,
  TaskStatus,
  TaskType,
  CreateEntityCategory,
  TaskUpdateRequest,
  TaskComment,
  CommentType,
  CommentCreateRequest,
  EvidenceWithUrl,
} from '../../models';
import { DOCUMENT_CONTENT_TYPES } from '../../models/document.model';
import { FRAMEWORK_METADATA, FrameworkType } from '../../models/framework.model';
import { AssigneePickerComponent } from '../assignee-picker/assignee-picker';
import { TaskApi } from '../../services/task-api/task-api';
import { LanguageBadgeComponent } from '../language-badge/language-badge';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { AuthService } from '../../services/auth/auth';
import { StateStoreService } from '../../services/state-store/state-store';
import { FeatureId } from '../../constants/features.constants';
import { EntityLinkService, LinkedEntityKind } from '../../services/entity-link/entity-link';
import { createAuditTrailLoader } from '../../shared/audit-trail-loader';
import { accessDenialMessage } from '../../utils/access-error.utils';
import { formatDate } from '../../utils/date.utils';
import { getInitials, getAvatarColor } from '../../utils/display.utils';
import { getTaskStatusVariant } from '../../utils/status-indicator.utils';

export type TaskDetailTab = 'overview' | 'comments' | 'evidence' | 'history';

@Component({
  selector: 'app-task-detail-panel',
  templateUrl: './task-detail-panel.html',
  styleUrl: './task-detail-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    TranslocoModule,
    ActionGlyphComponent,
    MatTooltipModule,
    DetailPanelComponent,
    EditablePanelTitleComponent,
    PanelTabsComponent,
    AuditTimelineComponent,
    RefreshButtonComponent,
    ConfirmModalComponent,
    ModalComponent,
    DatePickerDirective,
    SectionIconComponent,
    StatusIndicatorComponent,
    BadgeComponent,
    LanguageBadgeComponent,
    InlineTextComponent,
    SelectComponent,
    AssigneePickerComponent,
  ],
})
export class TaskDetailPanelComponent implements OnDestroy {
  readonly getTaskStatusMeaning = getTaskStatusMeaning;

  private readonly taskApi = inject(TaskApi);
  private readonly transloco = inject(TranslocoService);
  private readonly translationVersion = signal(0);
  private readonly api = inject(OrgManagementApi);
  private readonly authService = inject(AuthService);
  private readonly entityLink = inject(EntityLinkService);
  private readonly stateService = inject(StateStoreService);
  private readonly destroyRef = inject(DestroyRef);

  readonly task = input.required<Task>();
  readonly isOpen = input(false);
  readonly isManager = input(false);

  readonly initialTab = input<TaskDetailTab>('overview');
  readonly widthPx = model(480);
  readonly panelClose = output<void>();
  readonly taskUpdated = output<Task>();
  readonly taskDeleted = output<string>();

  readonly activeTab = signal<TaskDetailTab>('overview');

  readonly evidenceFiles = signal<EvidenceWithUrl[]>([]);
  readonly loadingEvidence = signal(false);
  readonly uploading = signal(false);
  readonly isDragging = signal(false);
  readonly isCompleting = signal(false);
  readonly completeError = signal<string | null>(null);
  readonly showCompleteConfirm = signal(false);
  readonly evidenceToDelete = signal<string | null>(null);
  readonly isDeletingEvidence = signal(false);

  /**
   * Upload / delete failures on the Evidence tab.
   *
   * Both handlers used to `console.error` and stop, so a refused upload
   * looked exactly like a successful one that produced no file — the tab
   * redrew unchanged with nothing to read. A read-only delegate hitting
   * `evidence_write` is the common case now that Tasks is its own access
   * area, and it is the one where silence is worst: the caller is not
   * failing, they are being told no.
   */
  readonly evidenceError = signal<string | null>(null);
  /** Whether {@link evidenceError} is a refusal rather than a failure —
   *  picks the amber lock banner over the red one. */
  private readonly _evidenceErrorIsDenial = signal(false);
  readonly evidenceErrorIsDenial = this._evidenceErrorIsDenial.asReadonly();

  /**
   * Split a failed write into the sentence to show and whether it was a
   * refusal rather than a failure.
   *
   * The chokepoint for every action on this panel: a 403 yields the server's
   * localised denial, anything else keeps `fallback`, because a network or
   * storage failure is not a permissions problem and must not read like one.
   */
  private describeError(err: unknown, fallback: string): { message: string; isDenial: boolean } {
    const denial = accessDenialMessage(err, this.transloco);
    return {
      message: denial ?? (err as { message?: string } | null)?.message ?? fallback,
      isDenial: denial !== null,
    };
  }

  private setEvidenceError(err: unknown, fallback: string): void {
    const { message, isDenial } = this.describeError(err, fallback);
    this._evidenceErrorIsDenial.set(isDenial);
    this.evidenceError.set(message);
  }

  private setCommentError(err: unknown, fallback: string): void {
    const { message, isDenial } = this.describeError(err, fallback);
    this._commentErrorIsDenial.set(isDenial);
    this.commentError.set(message);
  }

  /**
   * May this caller attach or remove evidence on *this task's* org?
   *
   * Keyed on `evidence_write`, which since T7 belongs to the Tasks access
   * area — so a view-only grant on Tasks lands here. Asked against
   * `task().org_id` rather than the effective org because the panel can
   * render a task belonging to a customer the caller is managing, and the
   * question is about the record on screen.
   *
   * Same legacy fallback as {@link canWriteTasks}, and for the same reason:
   * `isManager()` tested the org's *type*, so an ADMIN of a STANDARD org was
   * refused an upload the server would have accepted. The live gate is
   * `evidence_management_handler/src/main.py` — `require_org_scope(...,
   * EVIDENCE_WRITE)` plus a write-mode check, no role check beyond it — and
   * `evidence_write` sits in crp-be's `_COMPLIANCE_SCOPED_WRITES`, granted on
   * `_can_act_on_compliance` alone, which is true for a STANDARD caller on
   * its own org (VER-326 / T8).
   *
   * `canManageCompliance()` here is `canWrite(FeatureId.TASKS)`, mirroring
   * the server's own write-mode axis: evidence hangs off a task, so a
   * READ-only `tasks` feature blocks the upload on both sides.
   */
  readonly canWriteEvidence = computed<boolean>(() =>
    this.stateService.holdsCapabilityIn(
      this.task().org_id,
      'evidence_write',
      this.canManageCompliance()
    )
  );

  /**
   * May this caller write to *this task* — comments and the inline-edit
   * cells included?
   *
   * `task_comment_handler` gates its writes on `task_write` and its reads on
   * `task_read`, so a comment is a task write, not a separate axis: someone
   * granted view-only on Tasks can read the thread and add nothing to it.
   * Same org question as {@link canWriteEvidence}.
   *
   * The legacy fallback is `canManageCompliance()` — NOT `isManager()`, which
   * is what it was until this line was fixed. `isManager()` tests the org's
   * *type* (`org_type` is MANAGER or SUPER_MANAGER), so it is false for every
   * member of a STANDARD org including its ADMIN, and the whole inline-edit
   * surface went read-only for them. The backend has never agreed: task
   * mutations funnel through `_gate_task_write`, which pairs
   * `require_org_scope(..., TASK_WRITE)` with `require_feature_access(...,
   * write=True)`, and `task_write`'s legacy scope answer
   * (crp-be `access/legacy.py`, `_can_act_on_compliance`) has permitted a
   * STANDARD caller on its own org since VER-326. So an org admin was being
   * refused here on a write the server would have accepted.
   *
   * `canManageCompliance()` on this panel is `canWrite(FeatureId.TASKS)`,
   * which is the entitlement half of that same server pairing — the same
   * legacy answer `control-detail-panel.canWriteControl` passes.
   */
  readonly canWriteTasks = computed<boolean>(() =>
    this.stateService.holdsCapabilityIn(
      this.task().org_id,
      'task_write',
      this.canManageCompliance()
    )
  );

  /** Tooltip for the neutralised upload zone. Empty when permitted, so the
   *  directive renders nothing rather than an empty bubble. */
  readonly evidenceDisabledReason = computed<string>(() => {
    this.translationVersion();
    return this.canWriteEvidence()
      ? ''
      : this.transloco.translate('disabled_tooltips.no_write_capability_tasks');
  });

  /** As above, for the comment composer. Same copy — both refusals are the
   *  Tasks area saying no, and inventing a second sentence for the same
   *  answer would imply a second setting to change. */
  readonly commentDisabledReason = computed<string>(() => {
    this.translationVersion();
    return this.canWriteTasks()
      ? ''
      : this.transloco.translate('disabled_tooltips.no_write_capability_tasks');
  });

  readonly showCompletedAtPicker = computed(
    () => this.task().backfilled === true && this.stateService.canManageCompliance() === true
  );

  readonly backfilledCompletedAt = signal('');

  readonly canComplete = computed(() => {
    const uploadOk = this.task().task_type !== TaskType.UPLOAD || this.evidenceFiles().length > 0;
    const dateOk = !this.showCompletedAtPicker() || !!this.backfilledCompletedAt();
    return uploadOk && dateOk;
  });

  readonly canCompleteTooltip = computed(() => {
    if (this.task().task_type === TaskType.UPLOAD && this.evidenceFiles().length === 0) {
      return 'You need to provide at least one piece of evidence';
    }
    if (this.showCompletedAtPicker() && !this.backfilledCompletedAt()) {
      return 'Please select a completion date';
    }
    return '';
  });

  readonly pendingAction = signal<'approve' | 'reject' | null>(null);
  readonly actionComment = signal('');
  readonly isActioning = signal(false);
  readonly actionError = signal<string | null>(null);

  readonly approveModalConfig = computed<ModalConfig>(() => ({
    title: 'Approve task',
    size: 'small',
    showFooter: true,
    primaryButtonText: 'Approve',
    secondaryButtonText: 'Cancel',
    primaryButtonLoading: this.isActioning(),
  }));

  readonly rejectModalConfig = computed<ModalConfig>(() => ({
    title: 'Reject task',
    size: 'small',
    showFooter: true,
    primaryButtonText: 'Reject',
    secondaryButtonText: 'Cancel',
    primaryButtonDisabled: !this.actionComment().trim(),
    primaryButtonLoading: this.isActioning(),
  }));

  readonly panelTabs = computed<PanelTab[]>(() => {
    this.translationVersion();
    const t = (k: string) => this.transloco.translate<string>(k);
    return [
      { id: 'overview', label: t('panel_task.tab_overview') },
      { id: 'comments', label: t('panel_task.tab_comments') },
      ...(this.task().task_type === TaskType.UPLOAD
        ? [
            {
              id: 'evidence',
              label: t('panel_task.tab_evidence'),
              count: this.evidenceFiles().length || undefined,
            } as PanelTab,
          ]
        : []),
      { id: 'history', label: t('panel_task.tab_history') },
    ];
  });

  readonly auditLoader = createAuditTrailLoader({
    getOrgId: () => this.task().org_id,
    entityType: 'task',
    getEntityId: () => this.task().task_id,
    api: (orgId, type, id) => this.api.getAuditTrail(orgId, type, id),
  });

  // Delete state
  readonly showDeleteConfirm = signal(false);
  readonly isDeleting = signal(false);
  readonly deleteError = signal<string | null>(null);
  readonly todayDate = new Date().toISOString().split('T')[0];

  // ── Assignees ──
  protected readonly orgMembers = this.stateService.orgMembers;
  /** VER-326: write-authority for this surface — mode-aware.
   *  MANAGER / SUPER_MANAGER / SELF_MANAGED via role; STANDARD via
   *  the `tasks` feature access-mode override. Name preserved so the
   *  template bindings stay untouched. */
  protected readonly canManageCompliance = computed(() =>
    this.stateService.canWrite(FeatureId.TASKS)
  );
  /** Capability-gated, like every other inline-edit cell on this panel.
   *  Control-linked tasks stay editable here: the backend constrains the
   *  choice to the parent control's assignees rather than refusing the
   *  write, so the picker is still the right affordance. */
  protected readonly assigneeReadonly = computed(() => !this.canWriteTasks());

  /** Yes/No picker options for the Needs-approval inline cell. */
  readonly needsApprovalOptions = computed<readonly SelectOption<'yes' | 'no'>[]>(() => {
    this.translationVersion();
    return [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ];
  });

  // Comments state
  readonly comments = signal<TaskComment[]>([]);
  readonly commentsLoading = signal(false);
  readonly commentsError = signal<string | null>(null);
  readonly editingCommentId = signal<string | null>(null);
  readonly editingCommentContent = signal('');
  readonly savingComment = signal(false);
  readonly addingComment = signal(false);
  readonly newCommentContent = signal('');
  readonly commentError = signal<string | null>(null);
  /** Whether {@link commentError} is a refusal rather than a failure — same
   *  amber-lock / red split the evidence tab and the task forms use. */
  private readonly _commentErrorIsDenial = signal(false);
  readonly commentErrorIsDenial = this._commentErrorIsDenial.asReadonly();
  readonly addCommentExpanded = signal(false);
  readonly commentToDelete = signal<string | null>(null);
  readonly commentsOrder = signal<'ASC' | 'DESC'>('DESC');
  readonly newCommentInput = viewChild<ElementRef<HTMLTextAreaElement>>('newCommentInput');

  readonly currentUserId = computed(() => this.authService.user()?.userId ?? null);

  readonly noteComments = computed(() =>
    this.comments().filter(c => c.comment_type === CommentType.NOTE)
  );

  readonly latestStatusComment = computed(() => {
    const taskStatus = this.task().status;
    if (taskStatus === TaskStatus.WAITING_FOR_APPROVAL || taskStatus === TaskStatus.CREATED) {
      return null;
    }
    const targetType =
      taskStatus === TaskStatus.REJECTED ? CommentType.REJECTION : CommentType.APPROVAL;
    const matching = this.comments().filter(c => c.comment_type === targetType);
    if (!matching.length) return null;
    return [...matching].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  });

  readonly statusCommentExpanded = signal(false);

  readonly sources = computed<
    { id: string; name: string; label: string; icon: string; kind: LinkedEntityKind | null }[]
  >(() => {
    const t = this.task();
    const result: {
      id: string;
      name: string;
      label: string;
      icon: string;
      kind: LinkedEntityKind | null;
    }[] = [];
    if (t.control_id)
      result.push({
        id: t.control_id,
        name: t.control_name ?? t.control_id,
        label: 'Control',
        icon: 'assignment_turned_in',
        kind: 'control',
      });
    if (t.document_id)
      result.push({
        id: t.document_id,
        name: t.document_name ?? t.document_id,
        label: 'Document',
        icon: 'description',
        kind: 'document',
      });
    if (t.assessment_id)
      result.push({
        id: t.assessment_id,
        name: t.assessment_name ?? t.assessment_id,
        label: 'Assessment',
        icon: 'rate_review',
        kind: 'assessment',
      });
    if (t.framework_id)
      result.push({
        id: t.framework_id,
        name: t.framework_name ?? t.framework_id,
        label: 'Framework',
        icon: 'grid_view',
        kind: 'framework',
      });
    if (t.vendor_id)
      result.push({
        id: t.vendor_id,
        name: t.vendor_name ?? t.vendor_id,
        label: 'Vendor',
        icon: 'groups',
        kind: null,
      });
    if (t.linked_task_id)
      result.push({
        id: t.linked_task_id,
        name: t.linked_task_name ?? t.linked_task_id,
        label: 'Linked Task',
        icon: 'task_alt',
        kind: 'task',
      });
    return result;
  });

  readonly category = computed(() => {
    const sources = this.sources();
    if (sources.length > 0) return sources[0].label;
    return this.task().created_by_name === 'System' ? 'System' : 'General';
  });

  readonly taskTypeLabel = computed(() => {
    switch (this.task().task_type) {
      case TaskType.UPLOAD:
        return 'Upload';
      case TaskType.REVIEW:
        return 'Review';
      case TaskType.ASSESS:
        return 'Assess';
      case TaskType.APPROVE:
        return 'Approve';
      case TaskType.CREATE:
        return 'Create';
      case TaskType.ONBOARDING:
        return 'Onboarding';
      case TaskType.DELETE:
        return 'Delete';
      default:
        return 'Other';
    }
  });

  readonly createActionLabel = computed(() => {
    const t = this.task();
    if (t.task_type !== TaskType.CREATE) return null;
    const category = t.create_entity_category;
    const subtype = t.create_entity_subtype;
    if (!category) return null;
    switch (category) {
      case CreateEntityCategory.DOCUMENT: {
        const opt = DOCUMENT_CONTENT_TYPES.find(d => d.value === subtype);
        return opt ? `Create a ${opt.label}` : 'Create a document';
      }
      case CreateEntityCategory.ASSESSMENT:
        return 'Create an AI Risk Assessment';
      case CreateEntityCategory.FRAMEWORK: {
        const meta = subtype ? FRAMEWORK_METADATA[subtype as FrameworkType] : null;
        return meta ? `Create a ${meta.defaultName} framework` : 'Create a framework';
      }
      case CreateEntityCategory.CONTROL:
        return 'Create a new control';
      case CreateEntityCategory.VENDOR:
        return 'Create a new vendor';
      default:
        return null;
    }
  });

  readonly getTaskStatusVariant = getTaskStatusVariant;

  readonly statusLabel = computed(() => {
    this.translationVersion();
    return this.transloco.translate(getTaskStatusLabelKey(this.task().status));
  });

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));

    // Reset state + heavy reload (comments, evidence, audit) only fires
    // when the selected task actually changes — tracked by `task_id`.
    // Inline field edits push a NEW `Task` reference into the parent
    // signal via `(taskUpdated)` and would otherwise re-trigger every
    // GET on each commit and reset things like `activeTab` mid-edit.
    let prevTaskId: string | null = null;
    effect(() => {
      const id = this.task().task_id;
      if (id === prevTaskId) return;
      prevTaskId = id;
      this.showDeleteConfirm.set(false);
      this.deleteError.set(null);
      this.activeTab.set(untracked(() => this.initialTab()));
      this.editingCommentId.set(null);
      this.newCommentContent.set('');
      this.commentError.set(null);
      this.evidenceError.set(null);
      this.statusCommentExpanded.set(false);
      this.showCompleteConfirm.set(false);
      this.backfilledCompletedAt.set('');
      untracked(() => {
        this.loadComments();
        if (this.task().task_type === TaskType.UPLOAD) {
          this.loadEvidence();
        }
      });
      this.auditLoader.load();
    });
  }

  ngOnDestroy(): void {
    this.auditLoader.destroy();
  }

  setTab(tab: TaskDetailTab): void {
    this.activeTab.set(tab);
  }

  // ── Inline field editing ──

  /** Generic per-field PATCH for the overview tab's inline-edit cells.
   *  Emits the full server response so server-computed fields (e.g.
   *  `backfilled`) are reflected immediately without a page reload. */
  private updateField(request: TaskUpdateRequest): void {
    const t = this.task();
    this.taskApi
      .updateTask(t.task_id, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedTask: Task) => {
          this.taskUpdated.emit(updatedTask);
        },
        error: err => console.error('Failed to update task:', err),
      });
  }

  onDescriptionCommit(value: string): void {
    if (!this.canWriteTasks()) return;
    const next = value.trim();
    if (next === (this.task().description ?? '')) return;
    this.updateField({ description: next || null });
  }

  onStartDateChange(value: string): void {
    if (!this.canManageCompliance()) return;
    const next = value || null;
    if (next === (this.task().start_date ?? null)) return;
    this.updateField({ start_date: next });
  }

  onDueDateChange(value: string): void {
    if (!this.canManageCompliance() || this.task().recurring) return;
    const next = value || null;
    if (next === (this.task().due_date ?? null)) return;
    this.updateField({ due_date: next });
  }

  onNeedsApprovalChange(value: SelectValue<'yes' | 'no'>): void {
    if (!this.canWriteTasks()) return;
    const next = coerceSingleSelectValue(value);
    if (next == null) return;
    const nextBool = next === 'yes';
    if (nextBool === this.task().needs_approval) return;
    this.updateField({ needs_approval: nextBool });
  }

  onAssigneesChange(assigneeIds: string[]): void {
    this.updateField({ assignees: assigneeIds });
  }

  /** Commit a header-driven rename. Independent of the full Edit form —
   *  fires from `<app-editable-panel-title>` on blur/Enter when the name
   *  actually changed. */
  protected onTitleCommit(newName: string): void {
    const t = this.task();
    if (!this.canWriteTasks()) return;
    this.taskApi
      .updateTask(t.task_id, { name: newName })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.taskUpdated.emit({ ...t, name: newName }),
        error: err => console.error('Failed to rename task:', err),
      });
  }

  close(): void {
    this.panelClose.emit();
  }

  openLinkedEntity(kind: LinkedEntityKind, id: string): void {
    this.entityLink.navigateTo(kind, id, this.task().org_id);
    this.panelClose.emit();
  }

  confirmDelete(): void {
    this.isDeleting.set(true);
    this.deleteError.set(null);
    this.taskApi
      .deleteTask(this.task().task_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isDeleting.set(false);
          this.showDeleteConfirm.set(false);
          this.taskDeleted.emit(this.task().task_id);
          this.panelClose.emit();
        },
        error: err => {
          this.isDeleting.set(false);
          this.deleteError.set(err?.message || 'Failed to delete task');
        },
      });
  }

  loadEvidence(): void {
    this.loadingEvidence.set(true);
    this.taskApi
      .getTaskEvidence(this.task().task_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: evidence => {
          this.evidenceFiles.set(evidence);
          this.loadingEvidence.set(false);
        },
        error: err => {
          console.error('Failed to load evidence:', err);
          this.loadingEvidence.set(false);
        },
      });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    // No drag highlight without the capability — the zone must not look
    // live under the cursor when the drop will be ignored.
    if (!this.canWriteEvidence()) return;
    this.isDragging.set(true);
  }

  onDragLeave(): void {
    this.isDragging.set(false);
  }

  /**
   * Drag-and-drop is the reason this needs its own guard: disabling the file
   * input stops the click path, but a drop lands on the label and never
   * touches the input at all.
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
    if (!this.canWriteEvidence()) return;
    const files = event.dataTransfer?.files;
    if (files) this.processFiles(files);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.processFiles(input.files);
      input.value = '';
    }
  }

  openFile(file: EvidenceWithUrl): void {
    if (!file.download_url) return;
    window.open(file.download_url, '_blank');
  }

  getFileType(evidence: EvidenceWithUrl): 'PDF' | 'IMG' | 'DOC' | 'OTHER' {
    const ext = evidence.file_name?.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'pdf') return 'PDF';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'IMG';
    if (['doc', 'docx'].includes(ext)) return 'DOC';
    return 'OTHER';
  }

  openActionModal(action: 'approve' | 'reject'): void {
    this.actionComment.set('');
    this.actionError.set(null);
    this.pendingAction.set(action);
  }

  cancelAction(): void {
    this.pendingAction.set(null);
    this.actionComment.set('');
    this.actionError.set(null);
  }

  submitAction(): void {
    const action = this.pendingAction();
    if (!action) return;
    const t = this.task();
    const comment = this.actionComment().trim();
    if (action === 'reject' && !comment) return;

    this.isActioning.set(true);
    this.actionError.set(null);

    const request$ =
      action === 'approve'
        ? this.taskApi.approveTask(t.task_id, comment || undefined)
        : this.taskApi.rejectTask(t.task_id, comment);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isActioning.set(false);
        this.pendingAction.set(null);
        const newStatus = action === 'approve' ? TaskStatus.COMPLETED : TaskStatus.REJECTED;
        this.taskUpdated.emit({ ...t, status: newStatus });
      },
      error: err => {
        this.isActioning.set(false);
        this.actionError.set(err?.message || `Failed to ${action} task`);
      },
    });
  }

  confirmDeleteEvidence(): void {
    const evidenceId = this.evidenceToDelete();
    if (!evidenceId) return;
    this.isDeletingEvidence.set(true);
    this.evidenceError.set(null);
    this.taskApi
      .deleteTaskEvidence(this.task().task_id, evidenceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isDeletingEvidence.set(false);
          this.evidenceToDelete.set(null);
          this.loadEvidence();
        },
        error: err => {
          console.error('Failed to delete evidence:', err);
          this.isDeletingEvidence.set(false);
          // Close the confirm either way — leaving it open on a refusal
          // invites a second click at the same answer. The banner behind it
          // carries the reason.
          this.evidenceToDelete.set(null);
          this.setEvidenceError(err, 'Failed to delete evidence');
        },
      });
  }

  readonly completeConfirmMessage = computed(() => {
    const t = this.task();
    if (t.status === 'REJECTED')
      return 'By resubmitting this task, you confirm that all required actions have been completed and you accept responsibility for the accuracy of this submission.';
    if (t.needs_approval)
      return 'By submitting this task for approval, you confirm that all required actions have been completed and you accept responsibility for the accuracy of this submission.';
    return 'By marking this task as complete, you confirm that all required actions have been completed and you accept responsibility for the accuracy of this submission.';
  });

  readonly completeConfirmText = computed(() => {
    const t = this.task();
    if (t.status === 'REJECTED') return 'Submit for Review';
    if (t.needs_approval) return 'Submit for Approval';
    return 'Complete';
  });

  completeTask(): void {
    this.showCompleteConfirm.set(false);
    const t = this.task();
    this.isCompleting.set(true);
    this.completeError.set(null);
    const completedAt = this.showCompletedAtPicker()
      ? this.backfilledCompletedAt() || undefined
      : undefined;
    this.taskApi
      .completeTask(t.task_id, completedAt)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isCompleting.set(false);
          const newStatus = t.needs_approval
            ? TaskStatus.WAITING_FOR_APPROVAL
            : TaskStatus.COMPLETED;
          this.taskUpdated.emit({ ...t, status: newStatus });
        },
        error: err => {
          this.isCompleting.set(false);
          this.completeError.set(err?.message || 'Failed to complete task');
        },
      });
  }

  private processFiles(files: FileList): void {
    if (files.length === 0) return;
    // Belt-and-braces: both entry points already check, but this is the one
    // place the request is actually issued.
    if (!this.canWriteEvidence()) return;
    this.uploading.set(true);
    this.evidenceError.set(null);
    let pending = files.length;
    Array.from(files).forEach(file => {
      this.taskApi
        .uploadTaskEvidence(this.task().task_id, file)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            pending--;
            if (pending === 0) {
              this.uploading.set(false);
              this.loadEvidence();
            }
          },
          error: err => {
            console.error('Failed to upload evidence:', err);
            // First error wins for the message — with a multi-file drop the
            // refusal is the same for every file, so overwriting would just
            // re-render the same sentence N times.
            if (!this.evidenceError()) {
              this.setEvidenceError(err, 'Failed to upload evidence');
            }
            pending--;
            if (pending === 0) this.uploading.set(false);
          },
        });
    });
  }

  formatDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    return formatDate(iso, 'compact') || '—';
  }

  formatDateTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    return formatDate(iso, 'compactWithTime') || '—';
  }

  isEdited(comment: TaskComment): boolean {
    return (
      Math.abs(new Date(comment.updated_at).getTime() - new Date(comment.created_at).getTime()) >
      1000
    );
  }

  getInitials(name: string): string {
    return getInitials(name);
  }

  getAvatarColor(name: string): string {
    return getAvatarColor(name);
  }

  statusCommentTitle(comment: TaskComment): string {
    return comment.comment_type === CommentType.REJECTION
      ? `Changes requested by ${comment.created_by_name}`
      : `Approved by ${comment.created_by_name}`;
  }

  // ── Comments ──

  canModifyComment(comment: TaskComment): boolean {
    return comment.created_by === this.currentUserId();
  }

  toggleCommentsOrder(): void {
    this.commentsOrder.update(o => (o === 'DESC' ? 'ASC' : 'DESC'));
    this.loadComments();
  }

  loadComments(): void {
    this.commentsLoading.set(true);
    this.commentsError.set(null);
    this.taskApi
      .getComments(this.task().task_id, this.commentsOrder())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: comments => {
          this.comments.set(comments);
          this.commentsLoading.set(false);
        },
        error: err => {
          this.commentsError.set(err?.message || 'Failed to load comments');
          this.commentsLoading.set(false);
        },
      });
  }

  startEditComment(comment: TaskComment): void {
    this.editingCommentId.set(comment.comment_id);
    this.editingCommentContent.set(comment.content);
    this.commentError.set(null);
  }

  cancelCommentEdit(): void {
    this.editingCommentId.set(null);
    this.editingCommentContent.set('');
  }

  saveCommentEdit(commentId: string): void {
    const content = this.editingCommentContent().trim();
    if (!content) return;
    if (!this.canWriteTasks()) return;
    this.savingComment.set(true);
    this.commentError.set(null);
    this.taskApi
      .updateComment(this.task().task_id, commentId, { content })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingComment.set(false);
          this.editingCommentId.set(null);
          this.loadComments();
        },
        error: err => {
          this.savingComment.set(false);
          this.setCommentError(err, 'Failed to update comment');
        },
      });
  }

  deleteComment(commentId: string): void {
    if (!this.canWriteTasks()) return;
    this.commentToDelete.set(commentId);
  }

  confirmDeleteComment(): void {
    const commentId = this.commentToDelete();
    if (!commentId) return;
    this.commentError.set(null);
    this.taskApi
      .deleteComment(this.task().task_id, commentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.commentToDelete.set(null);
          this.loadComments();
        },
        error: err => {
          this.commentToDelete.set(null);
          this.setCommentError(err, 'Failed to delete comment');
        },
      });
  }

  openAddComment(): void {
    if (!this.canWriteTasks()) return;
    this.addCommentExpanded.set(true);
    setTimeout(() => this.newCommentInput()?.nativeElement.focus(), 0);
  }

  cancelAddComment(): void {
    this.newCommentContent.set('');
    this.addCommentExpanded.set(false);
  }

  addComment(): void {
    const content = this.newCommentContent().trim();
    if (!content || this.addingComment()) return;
    if (!this.canWriteTasks()) return;
    this.addingComment.set(true);
    this.commentError.set(null);
    const request: CommentCreateRequest = { content, comment_type: CommentType.NOTE };
    this.taskApi
      .createComment(this.task().task_id, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.addingComment.set(false);
          this.newCommentContent.set('');
          this.addCommentExpanded.set(false);
          this.loadComments();
        },
        error: err => {
          this.addingComment.set(false);
          this.setCommentError(err, 'Failed to post comment');
        },
      });
  }
}
